import { Column, IconButton, StatusChip, StatusChipType, Table } from '@salecycle/ui-component-library';
import { useNavigate } from 'core/router/useNavigate';
import { TFunction } from 'i18next';
import { OffsiteCampaignStatus } from 'models';
import { OffsiteCampaign } from 'models/offsite/Campaign/OffsiteCampaign';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { BulkRetargetingRoutes } from 'sections/bulk-retargeting-campaigns/routes';
import { toTitleCase } from 'sections/utils/strings';

export interface PopulatedListViewProps {
  campaigns: OffsiteCampaign[];
}

export const PopulatedListView = ({ campaigns }: PopulatedListViewProps) => {
  const { t } = useTranslation('campaigns');

  return <Table data={campaigns} columns={columns(t)} />;
};

const columns = (t: TFunction): Column<OffsiteCampaign>[] => [
  {
    Header: t('bulkRetargetingCampaigns.list.campaignName'),
    accessor: 'name',
    Cell: props => props.original.name,
  },
  {
    Header: t('bulkRetargetingCampaigns.list.sendDate'),
    accessor: 'startTime',
    Cell: props => {
      const formattedDate = Intl.DateTimeFormat('en-GB', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      }).format(new Date(props.original.startTime));

      const formattedTime = Intl.DateTimeFormat('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
      }).format(new Date(props.original.startTime));

      return <>{props.original.startTime ? `${formattedDate} ${formattedTime}` : 'Immediately'}</>;
    },
  },
  {
    Header: t('bulkRetargetingCampaigns.list.status'),
    accessor: 'state',
    Cell: props => {
      /*
        We override the status if the campaign has been scheduled to be sent immediately.
        There is a small window where the campaign is in a scheduled state and it hasn't yet been triggered.
        We force the status to show as triggered here to ensure we don't confuse the user.

        We can't save the campaign as triggered to get arround this issue as the backend relies on
        the status being Scheduled in order for the campaign to be picked up by batch worker.
      */
      const status =
        !props.original.startTime && props.original.state === OffsiteCampaignStatus.Scheduled
          ? OffsiteCampaignStatus.Triggered
          : props.original.state;

      return (
        <StatusChip
          aria-label={t('bulkRetargetingCampaigns.list.status')}
          label={status}
          variation={(toTitleCase(status) as unknown) as keyof StatusChipType}
        />
      );
    },
  },
  {
    Header: t('bulkRetargetingCampaigns.list.actions'),
    accessor: 'actions',
    Cell: props => {
      const { siteId } = useParams<{ siteId: string }>();
      const navigate = useNavigate();

      const redirectToEditor = useCallback(() => {
        navigate(BulkRetargetingRoutes.EDIT_AUDIENCE, { siteId, campaignId: props.original.id });
      }, [navigate, props.original.id, siteId]);

      return (
        <IconButton icon="pen" ariaLabel={t('bulkRetargetingCampaigns.list.editCampaign')} onClick={redirectToEditor} />
      );
    },
    width: 121,
  },
];
